/**
 * Das Profilbild -- zugeschnitten, bevor es losgeht (7.45b).
 *
 * Beschnitten und klein gerechnet wird hier und nicht auf dem Server, und das
 * ist die bessere Stelle: der Browser dekodiert, was er auch anzeigen kann.
 * Ein iPhone lädt HEIC hoch, und das öffnet ffmpeg ohne libheif gar nicht --
 * Safari zeigt es dagegen ohne Weiteres an. Nebenbei wandern zwanzig Kilobyte
 * über die Leitung statt vier Megapixel, und man sieht vorher, was man bekommt.
 *
 * Quadratisch aus der Mitte: das ist der Zuschnitt, den ein rundes Kürzel
 * ohnehin verlangt, und er braucht keine Bedienung.
 */

export const KANTE = 128;

export async function zuschneiden(datei: File): Promise<Blob> {
  const bild = await laden(datei);
  const kante = Math.min(bild.width, bild.height);
  if (kante === 0) throw new Error('Aus dieser Datei kommt kein Bild.');
  const leinwand = document.createElement('canvas');
  leinwand.width = KANTE;
  leinwand.height = KANTE;
  const stift = leinwand.getContext('2d');
  if (!stift) throw new Error('Dieser Browser kann das Bild nicht zuschneiden.');
  // Glatt verkleinern: ohne das sieht ein grosses Foto auf 128 px aus wie
  // gerastert.
  stift.imageSmoothingEnabled = true;
  stift.imageSmoothingQuality = 'high';
  stift.drawImage(bild, (bild.width - kante) / 2, (bild.height - kante) / 2, kante, kante, 0, 0, KANTE, KANTE);
  schliesse(bild);
  const blob = await new Promise<Blob | null>((fertig) => leinwand.toBlob(fertig, 'image/png'));
  if (!blob) throw new Error('Das Bild liess sich nicht schreiben.');
  return blob;
}

type Bildquelle = (HTMLImageElement | ImageBitmap) & { width: number; height: number };

async function laden(datei: File): Promise<Bildquelle> {
  // `createImageBitmap` ist der Weg, der auch HEIC nimmt, wo der Browser es
  // kann -- und er dreht nach EXIF, was ein Foto vom Telefon sonst quer legt.
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(datei, { imageOrientation: 'from-image' });
    } catch {
      // Ältere Browser kennen die Option nicht; dann eben der alte Weg.
    }
  }
  const url = URL.createObjectURL(datei);
  try {
    return await new Promise<HTMLImageElement>((fertig, fehler) => {
      const bild = new Image();
      bild.onload = () => fertig(bild);
      bild.onerror = () => fehler(new Error('Diese Datei ist kein Bild, das dieser Browser anzeigen kann.'));
      bild.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function schliesse(bild: Bildquelle): void {
  if ('close' in bild && typeof bild.close === 'function') bild.close();
}

/** Die Adresse des Bildes einer Person -- mit dem Zeitpunkt als Anhänger. */
export function bildUrl(userId: string, avatarAt: string | null | undefined): string | null {
  return avatarAt ? `/api/users/${userId}/bild?v=${encodeURIComponent(avatarAt)}` : null;
}
